/**
 * Hooks for managing document assignments
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { DocSidebarAssignment } from "@/lib/erp/docs";

/**
 * Assign a document to users and/or user groups with optional metadata
 */
export function useAssignDoc(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      users?: string[];
      user_groups?: string[];
      priority?: "Low" | "Medium" | "High" | "Urgent";
      complete_by?: string;
      comment?: string;
    }) => {
      return erp.docs.assignDoc({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query
      queryClient.setQueryData<{ assigned_to: DocSidebarAssignment[] }>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            assigned_to: data.assigned_to,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

/**
 * Unassign a user or user group from a document
 */
export function useUnassignDoc(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { user?: string; user_group?: string }) => {
      return erp.docs.unassignDoc({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query
      queryClient.setQueryData<{ assigned_to: DocSidebarAssignment[] }>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            assigned_to: data.assigned_to,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

/**
 * Assign current user to a document (convenience method)
 */
export function useAssignToMe(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: {
      priority?: "Low" | "Medium" | "High" | "Urgent";
      complete_by?: string;
      comment?: string;
    }) => {
      return erp.docs.assignToMe({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query
      queryClient.setQueryData<{ assigned_to: DocSidebarAssignment[] }>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            assigned_to: data.assigned_to,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

type UseAssignUserSuggestionsOptions = {
  enabled?: boolean;
  isOpen?: boolean;  // Dropdown open?
  minLength?: number;  // Default 0 to allow empty search
};

/**
 * Get user suggestions for assignment autocomplete
 */
export function useAssignUserSuggestions(
  searchText: string,
  options: UseAssignUserSuggestionsOptions = {}
) {
  const { enabled = true, isOpen = false, minLength = 0 } = options;

  const effectiveEnabled =
    enabled &&
    isOpen &&
    (searchText.length >= minLength || (searchText.length === 0 && minLength === 0));

  return useQuery({
    queryKey: ["assign-user-suggestions", searchText],
    queryFn: () => erp.docs.searchAssignUsers(searchText, 10),
    enabled: effectiveEnabled,
    staleTime: 30_000,  // Cache for 30 seconds
  });
}

type UseAssignGroupSuggestionsOptions = {
  enabled?: boolean;
  isOpen?: boolean;  // Dropdown open?
  minLength?: number;  // Default 0 to allow empty search
};

/**
 * Get user group suggestions for assignment autocomplete
 */
export function useAssignGroupSuggestions(
  searchText: string,
  options: UseAssignGroupSuggestionsOptions = {}
) {
  const { enabled = true, isOpen = false, minLength = 0 } = options;

  const effectiveEnabled =
    enabled &&
    isOpen &&
    (searchText.length >= minLength || (searchText.length === 0 && minLength === 0));

  return useQuery({
    queryKey: ["assign-group-suggestions", searchText],
    queryFn: () => erp.docs.searchAssignUserGroups(searchText, 10),
    enabled: effectiveEnabled,
    staleTime: 30_000,  // Cache for 30 seconds
  });
}
